import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Tag, theme as antdTheme } from 'antd';

import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import {
  getOpportunityStatus,
  OPPORTUNITY_STATUS_META,
} from '@totem/components/ticketing/opportunityStatus';
import { isVisibleOpportunity } from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';

const DAY_MS = 1000 * 60 * 60 * 24;

type Gaps = {
  // Short field names, rendered as one "Missing: X, Y" line.
  missingFields: string[];
  // Full-sentence process/filing problems, each its own bullet line.
  flags: string[];
};

// This page's own ticket fetch is scoped to category=Optimize,
// subCategory=Opportunity (same as v2) — so an opportunity mis-filed OUT of
// that category never reaches this tab. Only the inverse (correctly filed,
// but missing/inconsistent data) is catchable here.
const evaluateGaps = (rec: TicketQueryResult): Gaps => {
  const opt = rec.ticket.extras?.optimize;

  if (!opt) {
    return {
      missingFields: [],
      flags: [
        'Categorized as an Optimize opportunity but has no opportunity data recorded.',
      ],
    };
  }

  const missingFields: string[] = [];
  if (!opt.levelOfEffort) missingFields.push('Effort');
  if (!opt.scopeOfReturn) missingFields.push('Scope of return');
  if (!opt.savingsType) missingFields.push('Savings type');
  if (!(opt.savingsPotential > 0)) missingFields.push('Savings value');
  if (!(opt.savingsTerm > 0)) missingFields.push('Savings term');

  const flags: string[] = [];

  const status = getOpportunityStatus(rec);
  const hasApproval =
    rec.ticket.approvalRequests?.some((req) => req.status === 'APPROVED') ===
    true;
  if ((status === 'Draft' || status === 'Pending Review') && hasApproval) {
    flags.push(
      `Customer approved on record, but opportunity status still "${status}".`,
    );
  }

  const meta = OPPORTUNITY_STATUS_META[status];
  if ((meta.funnelOrder ?? -1) >= 2 && !opt.stepsRequired) {
    flags.push('No recommended steps recorded for an approved opportunity.');
  }

  if (meta.isActive && rec.ticket.updatedAt) {
    const idleDays =
      (Date.now() - new Date(rec.ticket.updatedAt).getTime()) / DAY_MS;
    if (idleDays >= 30) {
      flags.push(`No activity in ${Math.round(idleDays)} days while active.`);
    }
  }

  return { missingFields, flags };
};

type GapRow = {
  ticket: TicketQueryResult;
  gaps: Gaps;
};

const OptimizeNeedsAttentionTab = () => {
  const { ticketData } = useContext(TicketingContext);
  const { useToken } = antdTheme;
  const { token } = useToken();

  const rows = useMemo<GapRow[]>(() => {
    const tickets = (ticketData?.tickets ?? []).filter(isVisibleOpportunity);
    return tickets
      .map((rec) => ({ ticket: rec, gaps: evaluateGaps(rec) }))
      .filter(
        (row) => row.gaps.missingFields.length > 0 || row.gaps.flags.length > 0,
      );
  }, [ticketData]);

  const missingCount = rows.filter(
    (row) => row.gaps.missingFields.length > 0,
  ).length;
  const flaggedCount = rows.filter((row) => row.gaps.flags.length > 0).length;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: token.colorTextTertiary,
            marginBottom: 4,
          }}
        >
          Data quality
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: token.colorText,
            marginBottom: 6,
            lineHeight: 1.2,
          }}
        >
          {rows.length === 0
            ? 'Nothing to clean up.'
            : `${rows.length} record${rows.length === 1 ? '' : 's'} need${rows.length === 1 ? 's' : ''} attention.`}
        </div>
        <div
          style={{
            fontSize: 14,
            color: token.colorTextSecondary,
            lineHeight: 1.6,
            maxWidth: '68ch',
          }}
        >
          {rows.length === 0
            ? `Every opportunity in this scope is fully scoped and correctly filed. Nothing needs correcting before the next review.`
            : `Every item below is something IB should correct in the portal before the next review. Until they're fixed, the affected opportunities cannot be ranked, scored on the effort × return grid, or counted toward savings at stake. Select a ticket number to open the record.`}
        </div>
      </div>

      {rows.length === 0 ? (
        <Card size="small">
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: token.colorTextTertiary,
            }}
          >
            Data quality
          </div>
          <div
            style={{
              fontSize: 13,
              color: token.colorTextSecondary,
              marginTop: 6,
            }}
          >
            Every opportunity in scope is fully scoped. Nothing to clean up.
          </div>
        </Card>
      ) : (
        <Card
          size="small"
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Before the next review</span>
              <Tag color="warning">IB action</Tag>
            </div>
          }
        >
          <div
            style={{
              fontSize: 12.5,
              color: token.colorTextSecondary,
              marginBottom: 12,
            }}
          >
            {missingCount}{' '}
            {missingCount === 1 ? 'opportunity is' : 'opportunities are'}{' '}
            missing fields needed for a decision
            {flaggedCount > 0
              ? ` · ${flaggedCount} ${flaggedCount === 1 ? 'has' : 'have'} a filing or process problem`
              : ''}
          </div>
          {rows.map((row) => (
            <div
              key={row.ticket.ticket.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr',
                gap: 12,
                padding: '12px 0',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <div>
                <Link
                  to={`/dashboard/optimize/opportunity/${row.ticket.ticket.id}`}
                  style={{ fontFamily: 'monospace', fontSize: 12.5 }}
                >
                  {row.ticket.ticket.number}
                </Link>
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13.5 }}>
                  {row.ticket.ticket.summary}{' '}
                  <span
                    style={{ fontWeight: 400, color: token.colorTextTertiary }}
                  >
                    · {row.ticket.building?.name ?? 'Unassigned building'}
                  </span>
                </div>
                {row.gaps.missingFields.length > 0 && (
                  <div
                    style={{
                      fontSize: 12.5,
                      color: '#B45309',
                      marginTop: 4,
                    }}
                  >
                    Missing: {row.gaps.missingFields.join(', ')}
                  </div>
                )}
                {row.gaps.flags.map((flag) => (
                  <div
                    key={flag}
                    style={{
                      fontSize: 12.5,
                      color: token.colorTextSecondary,
                      marginTop: 4,
                    }}
                  >
                    • {flag}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

export default OptimizeNeedsAttentionTab;
